import { useState } from "react";
import { useNavigate, useSearchParams, Navigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { useTitle } from "@/lib/format";
import AuthShell, { AuthLink } from "@/components/AuthShell";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export const homeFor = (user) => (user.role === "admin" ? "/admin" : user.role === "provider" ? "/provider-dashboard" : "/bookings");

export default function SignIn() {
  useTitle("Sign In");
  const { user, login } = useAuth();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const next = params.get("next");
  if (user) return <Navigate to={next || homeFor(user)} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const u = await login(email, password);
      navigate(next || homeFor(u), { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally { setBusy(false); }
  };

  return (
    <AuthShell title="Welcome back" subtitle="Sign in to manage your bookings or business."
      footer={<>New to Bookvera? <AuthLink to={`/sign-up${next ? `?next=${encodeURIComponent(next)}` : ""}`} testId="go-sign-up">Create an account</AuthLink></>}>
      <form onSubmit={submit} className="space-y-5" data-testid="sign-in-form">
        <div><Label>Email</Label><Input data-testid="sign-in-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1.5 h-12" /></div>
        <div><Label>Password</Label><Input data-testid="sign-in-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1.5 h-12" /></div>
        {error && <p className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700" data-testid="sign-in-error">{error}</p>}
        <button data-testid="sign-in-submit" disabled={busy} className="flex h-12 w-full items-center justify-center rounded-xl bg-sky-600 font-semibold text-white hover:bg-sky-700 disabled:opacity-60">
          {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : "Sign In"}
        </button>
      </form>
    </AuthShell>
  );
}
